import { useState, useMemo } from 'react';
import { products } from '@/mocks/products';
import { categories } from '@/mocks/categories';

interface FilterSidebarProps {
  selectedBrands: string[];
  onBrandToggle: (brand: string) => void;
  priceMin: string;
  priceMax: string;
  onPriceMinChange: (v: string) => void;
  onPriceMaxChange: (v: string) => void;
  selectedRating: number | null;
  onRatingChange: (rating: number | null) => void;
  selectedCategory: string | null;
  onCategoryChange: (cat: string | null) => void;
  inStockOnly: boolean;
  onInStockToggle: () => void;
}

export default function FilterSidebar({
  selectedBrands,
  onBrandToggle,
  priceMin,
  priceMax,
  onPriceMinChange,
  onPriceMaxChange,
  selectedRating,
  onRatingChange,
  selectedCategory,
  onCategoryChange,
  inStockOnly,
  onInStockToggle,
}: FilterSidebarProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    category: true,
    price: true,
    brand: true,
    rating: true,
    availability: true,
  });

  const toggleSection = (key: string) => {
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));
  };

  const brands = useMemo(() => {
    const map = new Map<string, number>();
    products.forEach((p) => {
      map.set(p.brand, (map.get(p.brand) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, []);

  const activeFiltersCount =
    selectedBrands.length +
    (priceMin || priceMax ? 1 : 0) +
    (selectedRating ? 1 : 0) +
    (selectedCategory ? 1 : 0) +
    (inStockOnly ? 1 : 0);

  const SectionHeader = ({ label, sectionKey }: { label: string; sectionKey: string }) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionKey)}
      className="flex items-center justify-between w-full py-2 text-sm font-semibold text-foreground-900"
    >
      {label}
      <i
        className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${openSections[sectionKey] ? 'rotate-180' : ''}`}
      />
    </button>
  );

  return (
    <aside className="w-full lg:w-64 flex-shrink-0 space-y-1">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-foreground-950">Filters</h3>
        {activeFiltersCount > 0 && (
          <button
            type="button"
            onClick={() => {
              selectedBrands.forEach((b) => onBrandToggle(b));
              onPriceMinChange('');
              onPriceMaxChange('');
              onRatingChange(null);
              onCategoryChange(null);
              if (inStockOnly) onInStockToggle();
            }}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            Clear All ({activeFiltersCount})
          </button>
        )}
      </div>

      {/* Categories */}
      <div className="border-b border-background-200/60">
        <SectionHeader label="Category" sectionKey="category" />
        {openSections.category && (
          <div className="pb-3 space-y-1 max-h-60 overflow-y-auto">
            <button
              type="button"
              onClick={() => onCategoryChange(null)}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm w-full text-left transition-colors ${
                !selectedCategory ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i className="ri-apps-line text-foreground-500" />
              All Categories
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => onCategoryChange(selectedCategory === cat.id ? null : cat.id)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm w-full text-left transition-colors ${
                  selectedCategory === cat.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <i className={`${cat.icon} text-foreground-500`} />
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Price Range */}
      <div className="border-b border-background-200/60">
        <SectionHeader label="Price Range" sectionKey="price" />
        {openSections.price && (
          <div className="pb-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-500">KSh</span>
                <input
                  type="number"
                  placeholder="Min"
                  value={priceMin}
                  onChange={(e) => onPriceMinChange(e.target.value)}
                  className="w-full pl-9 pr-2 py-2 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
              <span className="text-foreground-400">-</span>
              <div className="relative flex-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-foreground-500">KSh</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={priceMax}
                  onChange={(e) => onPriceMaxChange(e.target.value)}
                  className="w-full pl-9 pr-2 py-2 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { min: '0', max: '5000', label: 'Under 5K' },
                { min: '5000', max: '20000', label: '5K - 20K' },
                { min: '20000', max: '50000', label: '20K - 50K' },
                { min: '50000', max: '100000', label: '50K - 100K' },
                { min: '100000', max: '', label: 'Above 100K' },
              ].map((range) => {
                const isActive = priceMin === range.min && priceMax === range.max;
                return (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => {
                      if (isActive) {
                        onPriceMinChange('');
                        onPriceMaxChange('');
                      } else {
                        onPriceMinChange(range.min);
                        onPriceMaxChange(range.max);
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Brand */}
      <div className="border-b border-background-200/60">
        <SectionHeader label="Brand" sectionKey="brand" />
        {openSections.brand && (
          <div className="pb-3 space-y-1 max-h-60 overflow-y-auto">
            {brands.map(([brand, count]) => (
              <label
                key={brand}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(brand)}
                  onChange={() => onBrandToggle(brand)}
                  className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                />
                <span className="text-sm text-foreground-800 flex-1">{brand}</span>
                <span className="text-xs text-foreground-500">({count})</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Rating */}
      <div className="border-b border-background-200/60">
        <SectionHeader label="Rating" sectionKey="rating" />
        {openSections.rating && (
          <div className="pb-3 space-y-1">
            {[4, 3, 2, 1].map((rating) => (
              <button
                key={rating}
                type="button"
                onClick={() => onRatingChange(selectedRating === rating ? null : rating)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg w-full text-left transition-colors ${
                  selectedRating === rating ? 'bg-accent-50' : 'hover:bg-background-100'
                }`}
              >
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i
                      key={s}
                      className={`ri-star-fill text-xs ${s <= rating ? 'text-accent-500' : 'text-background-300'}`}
                    />
                  ))}
                </div>
                <span className="text-sm text-foreground-700">&amp; Up</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Availability */}
      <div className="border-b border-background-200/60">
        <SectionHeader label="Availability" sectionKey="availability" />
        {openSections.availability && (
          <div className="pb-3">
            <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={inStockOnly}
                onChange={onInStockToggle}
                className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
              />
              <span className="text-sm text-foreground-800">In Stock Only</span>
            </label>
          </div>
        )}
      </div>
    </aside>
  );
}